import { roomOutlinesToUnionPerimetersWithProvenance } from "building-generation/generationTools/generateFloorSystemBoundaryTools";
import { type SheetGeometry, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { isCompositeLevelContentSourceId, resolveContentSourceIdToLevelIds } from "doc/docViewport";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { formatInchesFraction, parseEvalFeetInchesFraction } from "doc/measurementUtils";
import type { PlanGenerationContextBase } from "doc/plans/planGeometrySource";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { intersectPolyLine2PolyLine2 } from "geom/intersect";
import { CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import { polygonSubtract, polygonUnion } from "geom/polygon";
import {
  polyLine2sAreCoincident,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2MergeCoincidentVertices,
  polyLine2EdgeEval,
} from "geom/polyLine2";
import type { PolyLine2Polygon, PolyLine2 } from "geom/polyLine2Types";
import { splitPolyLine2AtSplitPoints, splitPolyLine2ByRegions } from "geom/trim";

interface LevelPerimeterGeometry {
  readonly floorHeight: number;
  readonly originalPolygons: PolyLine2Polygon[];
  polygons: PolyLine2Polygon[];
}
function isPolyLine2(pl: PolyLine2 | undefined): pl is PolyLine2 {
  return !!pl;
}
function perimeterPathsToPolygons(paths: PolyLine2[]): PolyLine2Polygon[] {
  return paths.map((path) => [path]);
}
function pruneOverlappingSegments(subject: PolyLine2, trimmingPaths: PolyLine2[]): PolyLine2[] {
  if (trimmingPaths.length === 0) {
    return [subject];
  }
  if (trimmingPaths.some((trimmingPath) => polyLine2sAreCoincident(subject, trimmingPath))) {
    return [];
  }

  const edgeCount = polyLine2EdgeCount(subject);
  if (edgeCount === 0) {
    return [];
  }

  const splitPoints = [
    { edgeIndex: 0, t: 0 },
    { edgeIndex: edgeCount - 1, t: polyLine2EdgeLength(subject, edgeCount - 1) },
  ];
  for (const trimmingPath of trimmingPaths) {
    const intersections = intersectPolyLine2PolyLine2(subject, trimmingPath, DIST_EPSILON);
    for (const intersection of intersections) {
      if (intersection.int instanceof CurveCurveRangeIntersection) {
        splitPoints.push(
          { edgeIndex: intersection.edgeIndexA, t: intersection.int.startParam0 },
          { edgeIndex: intersection.edgeIndexA, t: intersection.int.endParam0 },
        );
      }
    }
  }

  return splitPolyLine2AtSplitPoints(subject, splitPoints)
    .map(({ component }) => polyLine2MergeCoincidentVertices(component))
    .filter((component) => {
      if (component.length < 4) {
        return false;
      }
      const [midX, midY] = polyLine2EdgeEval(component, 0, 0.5, true);
      return !trimmingPaths.some((trimmingPath) => {
        const containment = classifyContainmentVec2PolyLine2(midX, midY, trimmingPath, DIST_EPSILON);
        return containment === Vec2PolyLine2ContainmentClass.COINCIDENT;
      });
    });
}

export function makeSheetGeometryLevelHeightDifferenceLines({
  building,
  levelId,
}: PlanGenerationContextBase): SheetGeometry[] {
  const memberLevelIds = resolveContentSourceIdToLevelIds(building.doc, levelId, building.levels).filter(
    (memberLevelId) => {
      return (
        building.levels[memberLevelId] !== undefined &&
        building.walls[memberLevelId] !== undefined &&
        building.dimensionBehavior[memberLevelId] !== undefined
      );
    },
  );

  // Collect rooms with the same floor offset, rounded to 1/8"
  const roomsByFloorHeight: Record<string, PolyLine2[]> = {};
  for (const memberLevelId of memberLevelIds) {
    const rooms = roomsOnLevel(memberLevelId, building.rooms, building.roomsByLevel);
    const levelHeight = building.levels[memberLevelId].floorElevation;

    for (const room of rooms) {
      const floorAssembly = building.roomFloorAssemblies[room.id];
      const roomHeight = floorAssembly ? floorAssemblyTop(floorAssembly, building.levels[memberLevelId]) : levelHeight;
      // Using INSIDE_OF_MASS_IGNORE_VOID ensures that adjacent void wall segments are unioned together while
      // avoiding an edge case where a lone wall segment would be split by a floor height difference line.
      const roomOutline = building.roomOutlineTable[room.id]?.INSIDE_OF_MASS_IGNORE_VOID;
      if (roomOutline) {
        const roomHeightString = formatInchesFraction(roomHeight);
        (roomsByFloorHeight[roomHeightString] ??= []).push(roomOutline);
      }
    }
  }

  // Generate unioned geometry from each collection of rooms with the same offset
  const levelPerimeters: LevelPerimeterGeometry[] = [];
  for (const [floorHeight, roomOutlines] of Object.entries(roomsByFloorHeight)) {
    const { perimeters } = roomOutlinesToUnionPerimetersWithProvenance(roomOutlines);
    const perimeterPolygons = perimeterPathsToPolygons(perimeters);

    const floorHeightFloat = parseEvalFeetInchesFraction(floorHeight);

    if (perimeterPolygons.length === 0 || Number.isNaN(floorHeightFloat)) {
      continue;
    }

    levelPerimeters.push({
      floorHeight: floorHeightFloat,
      originalPolygons: perimeterPolygons,
      polygons: perimeterPolygons,
    });
  }

  if (levelPerimeters.length === 0) {
    return [];
  }

  levelPerimeters.sort((a, b) => a.floorHeight - b.floorHeight);

  const higherPolygonsByLowerIndex: PolyLine2Polygon[][] = [];
  let accumulatedHigherPolygons: PolyLine2Polygon[] = [];
  for (let perimeterIndex = levelPerimeters.length - 1; perimeterIndex >= 0; perimeterIndex--) {
    higherPolygonsByLowerIndex[perimeterIndex] = accumulatedHigherPolygons;
    const currentOriginalPolygons = levelPerimeters[perimeterIndex].originalPolygons;
    if (currentOriginalPolygons.length > 0) {
      accumulatedHigherPolygons = [...currentOriginalPolygons, ...accumulatedHigherPolygons];
    }
  }

  // Iterate low-to-high and subtract all higher level perimeters from each lower level in one pass.
  for (let lowerIndex = 0; lowerIndex < levelPerimeters.length; lowerIndex++) {
    const lowerPolygons = levelPerimeters[lowerIndex].polygons;
    const higherPolygons = higherPolygonsByLowerIndex[lowerIndex];
    if (higherPolygons.length === 0 || lowerPolygons.length === 0) {
      continue;
    }

    const nextLowerPolygons: PolyLine2Polygon[] = [];
    for (const lowerPolygon of lowerPolygons) {
      nextLowerPolygons.push(...polygonSubtract([lowerPolygon, ...higherPolygons]));
    }

    levelPerimeters[lowerIndex].polygons = nextLowerPolygons;
  }

  const exteriorPolygons = polygonUnion(levelPerimeters.flatMap((perimeter) => perimeter.originalPolygons));
  const exteriorLoops = exteriorPolygons.map((polygon) => polygon[0]).filter(isPolyLine2);
  if (exteriorLoops.length === 0) {
    return [];
  }
  const exteriorRegions: PolyLine2Polygon[] = exteriorLoops.map((loop) => [loop]);

  // Eliminate segments of the level perimeters that sit on the exterior of the home.
  // We only want to show the interior segments.
  const interiorPaths: PolyLine2[] = [];
  for (const perimeter of levelPerimeters) {
    const perimeterSegments = perimeter.polygons.flatMap((polygon) => polygon);
    for (const perimeterSegment of perimeterSegments) {
      const classifiedSegments = splitPolyLine2ByRegions(perimeterSegment, exteriorRegions, DIST_EPSILON);
      for (const { component, regionIndices } of classifiedSegments) {
        if (regionIndices.length === 0 || component.length < 4) {
          continue;
        }

        const [midX, midY] = polyLine2EdgeEval(component, 0, 0.5, true);
        const isInsideAnyRegion = regionIndices.some((regionIndex) => {
          const regionLoop = exteriorLoops[regionIndex];
          const midpointContainment = classifyContainmentVec2PolyLine2(midX, midY, regionLoop);
          return midpointContainment === Vec2PolyLine2ContainmentClass.INSIDE;
        });
        if (!isInsideAnyRegion) {
          continue;
        }

        if (interiorPaths.some((existingPath) => polyLine2sAreCoincident(existingPath, component))) {
          continue;
        }

        interiorPaths.push(...pruneOverlappingSegments(component, interiorPaths));
      }
    }
  }

  if (interiorPaths.length === 0) {
    return [];
  }

  const levelType = isCompositeLevelContentSourceId(building.doc, levelId) ? "COMPOSITE_LEVEL" : "LEVEL";

  return [
    sheetPolylineGeometryNew({
      handle: { type: levelType, id: levelId },
      paths: interiorPaths,
    }),
  ];
}
